import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/predictive-analytics.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const PredictiveAnalytics = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: '',
    dataType: 'sales'
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access predictive analytics services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New Predictive Analytics Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for predictive analytics services:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nDATA TYPE: ${contactInfo.dataType}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: '',
        dataType: 'sales'
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="predictive-analytics-page">
        <div className="hero-section">
          <h1>Predictive Analytics</h1>
          <p className="hero-subtitle">From guesswork to foresight — data-driven predictions for your business</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Anticipate What's Next</h2>
            <p className="section-intro">Our predictive analytics services help businesses move from guesswork to foresight — using data to anticipate customer behavior, sales trends, operational bottlenecks, or inventory needs.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Sales Forecasting</h3>
                <p>Accurately predict future sales by product, region, or customer segment</p>
              </div>
              <div className="service-item">
                <h3>Customer Behavior</h3>
                <p>Anticipate churn, identify upsell opportunities, and predict lifetime value</p>
              </div>
              <div className="service-item">
                <h3>Inventory Optimization</h3>
                <p>Reduce stockouts and overstock with demand forecasting</p>
              </div>
              <div className="service-item">
                <h3>Operational Efficiency</h3>
                <p>Predict bottlenecks and optimize resource allocation</p>
              </div>
            </div>
          </section>



          <section className="technology-section">
            <h2>Our Technology Stack</h2>
            <div className="tech-features">
              <div className="tech-item">
                <h4>🌲 Advanced ML Models</h4>
                <p>XGBoost, CatBoost, and ensemble methods for tabular data</p>
              </div>
              <div className="tech-item">
                <h4>⏱️ Time-Series Forecasting</h4>
                <p>LSTM, Prophet, and specialized forecasting algorithms</p>
              </div>
              <div className="tech-item">
                <h4>🔧 Hyperparameter Tuning</h4>
                <p>Optuna framework for optimal model configuration</p>
              </div>
              <div className="tech-item">
                <h4>🔄 Hybrid Approaches</h4>
                <p>Combining traditional ML with GenAI for complex scenarios</p>
              </div>
            </div>
          </section>

          <section className="use-cases">
            <h2>Industry Applications</h2>
            <div className="use-case-grid">
              <div className="use-case-item">
                <h3>Retail</h3>
                <p>Demand forecasting, inventory optimization, and personalized promotions</p>
              </div>
              <div className="use-case-item">
                <h3>Healthcare</h3>
                <p>Patient flow prediction, resource allocation, and early intervention</p>
              </div>
              <div className="use-case-item">
                <h3>Finance</h3>
                <p>Risk assessment, fraud detection, and market trend analysis</p>
              </div>
              <div className="use-case-item">
                <h3>Logistics</h3>
                <p>Route optimization, delivery time prediction, and maintenance forecasting</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Start Your Predictive Journey</h2>
            <p>Tell us about your business challenges, and we'll explore how predictive analytics can help you make better decisions.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={contactInfo.dataType}
                  onChange={(e) => handleContactInfoChange('dataType', e.target.value)}
                  className="form-select"
                >
                  <option value="sales">Sales Data</option>
                  <option value="customer">Customer Data</option>
                  <option value="operations">Operations Data</option>
                  <option value="inventory">Inventory Data</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea
                placeholder="Describe your business challenge or what you'd like to predict *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Development Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Data Assessment</h3>
                <p>We evaluate your data sources, quality, and predictive potential</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Model Development</h3>
                <p>Building and training custom predictive models for your specific needs</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Prescriptive Analysis</h3>
                <p>Converting predictions into actionable recommendations</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Integration & Optimization</h3>
                <p>Deploying models into your workflow and continuously improving accuracy</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default PredictiveAnalytics;